/**
 * Device fingerprint holder for web-viewer (PLT-2288).
 *
 * This is a deliberate restatement of the same contract web-editor ships, not a
 * shared module: neither repo depends on `@spectora/base-api-service`, and
 * adding that dependency to a Vue 2 app to obtain ~120 lines is not a trade
 * worth making. The CSRF token client (PLT-2102 / PLT-2103) exists in three
 * copies for exactly this reason. Keep the copies in step.
 *
 * The value is consumed by Rack::Attack's `api/fingerprint` and
 * `botnet/fingerprint` rules, which read it off `HTTP_X_FINGERPRINT` and
 * validate it through `Security::Fingerprint::Services::ValidationService`.
 * The backend stores `NULL` for anything invalid, and `NULL` is itself a
 * signal — so this module returns `null` rather than a placeholder or a
 * truncated value whenever it cannot produce a real visitor ID.
 */

/**
 * The `localStorage` key the cached visitor ID lives under.
 *
 * Namespaced and versioned: bumping the suffix is how a future change to the
 * cached shape invalidates every browser's entry without a migration.
 */
export const FINGERPRINT_STORAGE_KEY = 'spectora.viewer.fingerprint.v1'

/**
 * How long a computed visitor ID is reused before it is recomputed.
 *
 * Computing one costs roughly 100–500 ms on a cold browser, and this app's
 * population is anonymous consumers on phones opening a shared report link —
 * the traffic least able to absorb that on every page load. FingerprintJS's own
 * guidance is to cache for a day, which is also long enough that a device is
 * recognisable across a report-reading session.
 */
export const FINGERPRINT_TTL_MS = 24 * 60 * 60 * 1000

/**
 * What `Security::Fingerprint::Services::ValidationService.valid?` accepts:
 * 20 to 256 alphanumerics. A FingerprintJS visitor ID is 32 hex characters, so
 * a real one always passes; this guard is here so a future agent change, a
 * corrupted cache entry, or a hand-edited `localStorage` value can never put a
 * string on the wire that the backend will throw away as `NULL`.
 */
const VALID_VISITOR_ID = /^[a-zA-Z0-9]{20,256}$/

export const isValidVisitorId = (value) =>
  typeof value === 'string' && VALID_VISITOR_ID.test(value)

/**
 * The visitor ID for this page, held in **module scope** — deliberately not on
 * an axios instance.
 *
 * `src/api/index.js` forks the shared instance via `railsService.create({})`,
 * and both the shared instance and the fork must send the *same* value. Holding
 * it per-instance would mean two agents, two computes, and potentially two
 * different fingerprints inside one session.
 */
let visitorId = null

/**
 * The lazily-created `FingerprintJS.load()` promise, reused across every call.
 *
 * `load()` is the expensive one-time bootstrap; `agent.get()` is the per-call
 * compute. Caching the promise rather than the agent means concurrent first
 * callers share one bootstrap instead of racing two.
 *
 * A rejected promise is deliberately kept: when the script is blocked or the
 * agent cannot start, every later call should fail fast and return `null`
 * rather than retry the same failure on every request.
 */
let agentPromise = null

/**
 * The in-flight `getFingerprint()` promise, so concurrent callers — the shared
 * instance and the fork firing at once on page load — share one compute.
 */
let inFlight = null

/**
 * Reads the cached visitor ID, or `null` when there is no usable one.
 *
 * Every failure is a cache miss, never a throw: `localStorage` itself throws on
 * access in Safari private browsing and when a browser blocks storage for an
 * embedded context, and a malformed or expired entry tells us nothing.
 */
const readCache = () => {
  try {
    const raw = window.localStorage.getItem(FINGERPRINT_STORAGE_KEY)

    if (!raw) return null

    const { value, storedAt } = JSON.parse(raw)

    if (!isValidVisitorId(value)) return null
    if (typeof storedAt !== 'number') return null
    if (Date.now() - storedAt >= FINGERPRINT_TTL_MS) return null

    return value
  } catch (error) {
    return null
  }
}

/**
 * Caches a visitor ID. A storage failure is swallowed — the value is already
 * held in memory for this page, and losing the cross-page cache is not worth
 * failing a request over.
 */
const writeCache = (value) => {
  try {
    window.localStorage.setItem(
      FINGERPRINT_STORAGE_KEY,
      JSON.stringify({ value, storedAt: Date.now() })
    )
  } catch (error) {
    // Private browsing, storage disabled, or quota exhausted. Not fatal.
  }
}

/**
 * Loads the FingerprintJS agent once.
 *
 * `monitoring: false` is load-bearing (Key Decision 4). The OSS agent otherwise
 * fires a sampled XHR to `m1.openfpcdn.io` on 0.1% of `load()` calls
 * (`fp.esm.js:3196`, gated on `options.monitoring ?? true`). Opting out keeps
 * the agent fully local, which is also why this change needs no `connect_src`
 * entry as `CSP_MODE` rolls out.
 *
 * The agent is imported **dynamically**, so Vite splits it into its own chunk
 * and the ~30 KB module never reaches the boot bundle. That matters here more
 * than the raw number suggests: the flag is off in every environment, so a
 * static import would ship dead weight to every anonymous buyer opening a
 * shared report link on a phone — the traffic least able to absorb it. With the
 * flag on, the fetch happens on the first request that actually needs a
 * fingerprint, which is already behind an await.
 *
 * A rejected promise is still deliberately kept (see `agentPromise`): a blocked
 * script or a failed chunk fetch should fail fast on every later call rather
 * than re-fetch the same failure in front of every request.
 */
const loadAgent = () => {
  if (!agentPromise) {
    agentPromise = import('@fingerprintjs/fingerprintjs').then(
      ({ default: FingerprintJS }) => FingerprintJS.load({ monitoring: false })
    )
  }

  return agentPromise
}

/**
 * The device fingerprint for this page, or `null` when one cannot be produced.
 *
 * **Never throws and never rejects.** Every failure path — `load()` rejecting,
 * the agent throwing, `localStorage` throwing, a result that would not pass the
 * backend's validation — resolves to `null`, and the caller sends no header.
 * A request must complete normally whether or not a fingerprint exists.
 */
export const getFingerprint = async () => {
  if (visitorId) return visitorId

  const cached = readCache()

  if (cached) {
    visitorId = cached
    return visitorId
  }

  if (!inFlight) {
    inFlight = (async () => {
      try {
        const agent = await loadAgent()
        const { visitorId: computed } = await agent.get()

        if (!isValidVisitorId(computed)) return null

        visitorId = computed
        writeCache(computed)

        return visitorId
      } catch (error) {
        return null
      } finally {
        inFlight = null
      }
    })()
  }

  return inFlight
}

/**
 * Drops every cached value. Exists for tests — nothing in the app clears it,
 * because a fresh page load starts from an empty module scope anyway.
 */
export const resetFingerprint = () => {
  visitorId = null
  agentPromise = null
  inFlight = null
}
